import React, { FC, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { projectsApis } from "../../../api/projects";
import {
  ORG_MASTER_PROJECT_ID,
  ORG_MASTER_LABEL,
} from "../shared/ProjectScopeSelect";
import {
  Alert,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { LoadingOutlined, PlusSquareOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { normsApis } from "../../../api/Norms";
import materialsApis from "../../../api/master/materials";
import Swal from "sweetalert2";
import { ReactTyped } from "react-typed";
import { Descriptions, Flex, Popconfirm, Result, Spin, Table, Tag } from "antd";
import { nanoid } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import NormMaterialModal from "./NormMaterialModal";
import { Icon } from "../../../components/Component";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { CustomJsonParse } from "../../../utils/convertions";
import { ViewAccess } from "../../../components/accessControl/AccessControl";

// NOTE on wastage: the API stores wastage as a FRACTION (0.02 = 2%). The UI
// works in PERCENT everywhere — values are multiplied by 100 when a norm is
// loaded and divided by 100 when a payload is built.
export const wastageFractionToPercent = (value: any) =>
  value === undefined || value === null || value === ""
    ? value
    : Number((Number(value) * 100).toFixed(6));

export const wastagePercentToFraction = (value: any) =>
  value === undefined || value === null || value === ""
    ? undefined
    : Number((Number(value) / 100).toFixed(8));

export type NormMaterialType = {
  normMaterialId?: number;
  materialId: number | undefined;
  materialName?: string;
  materialCode?: string;
  uomId?: number;
  uomName?: string;
  pucFactor?: number;
  consumptionNorm: number | undefined;
  wastage?: number; // held as PERCENT in the UI
  unitPrice?: number;
  status?: string;
};

export interface NormSubmit {
  normCode: string;
  normTitle?: string;
  remarks?: string;
  // Scope of a NEW norm (0 = org master). Immutable once created — sent on
  // create only, never in the update payload.
  projectId?: number;
  materials: Array<NormMaterialType>;
}

export type NormFetchApiResponse = {
  normId: number;
  orgId: number;
  normCode: string;
  normTitle: string;
  remarks: string;
  status: "Active" | "Inactive";
  createdAt: any;
  updatedAt: any;
  createdBy: number;
  updatedBy: number;
  createdByUserName?: string;
  updatedByUserName?: string;
  paginationId?: number;
  normMaterials: NormMaterialType[];
};

const defaultMaterialData: NormMaterialType = {
  materialId: undefined,
  materialName: undefined,
  materialCode: undefined,
  uomId: undefined,
  uomName: undefined,
  pucFactor: 1,
  consumptionNorm: undefined,
  wastage: undefined,
  unitPrice: undefined,
};

const NormCreation: FC<{
  id: any;
  close: () => void;
  refresh: () => void;
  extra?: {
    defaultValues: NormSubmit;
  };
}> = ({ id, close, refresh, extra }) => {
  const [loading, setLoading] = useState(id ? true : false);
  const [originalData, setOriginalData] = useState<NormFetchApiResponse>();
  const [mode, setMode] = useState<"create" | "edit" | "view">(
    id ? "view" : "create"
  );
  const [screenSize, setScreenSize] = useState(0);
  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);
  const [uomOptions, setUomOptions] = useState<SelectOption[]>([]);
  // Project-scope options for CREATE mode only (org master + the org's projects).
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([
    { value: ORG_MASTER_PROJECT_ID, label: ORG_MASTER_LABEL },
  ]);
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );

  const [warningNorm, setWarningNorm] = useState<{
    error: any | undefined;
    message: string | undefined;
  }>({
    error: undefined,
    message: undefined,
  });

  const [materialModalStatus, setMaterialModalStatus] = useState<{
    open: boolean;
    title: "Add New Material" | "Edit Material";
    data: NormMaterialType;
    index: number | undefined;
  }>({
    open: false,
    title: "Add New Material",
    data: { ...defaultMaterialData },
    index: undefined,
  });

  const methods = useForm<NormSubmit>({
    mode: "onChange",
  });

  const { control, handleSubmit, setValue } = methods;
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "materials",
  });

  const resetWarningNorm = () => {
    setWarningNorm({
      error: undefined,
      message: undefined,
    });
  };

  const mapNormMaterialsToForm = (normMaterials: any[]) =>
    normMaterials?.map((item: any) => ({
      ...item,
      id: item.normMaterialId,
      wastage: wastageFractionToPercent(item?.wastage),
    })) as NormMaterialType[];

  const resettingFields = (originalData: any) => {
    try {
      resetWarningNorm();
      const { normCode, normTitle, remarks, normMaterials } = originalData;
      setValue("normCode", normCode);
      setValue("normTitle", normTitle);
      setValue("remarks", remarks);
      setValue("materials", mapNormMaterialsToForm(normMaterials));
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterialsList = async () => {
    try {
      const response = await materialsApis.fetchProduct();
      const { data: optionsArray, status } = response;
      if (status) {
        const activeMaterials = optionsArray?.filter((item: any) =>
          item?.status ? item?.status === "Active" : true
        );
        setMaterialOptions(
          activeMaterials?.map((item: any) => ({
            value: item?.materialID ?? item?.materialId,
            label: item?.productCode
              ? `${item?.productName} (${item?.productCode})`
              : item?.productName,
            extra: {
              materialCode: item?.productCode,
              uomId: item?.unitOfMeasure
                ? Number(item?.unitOfMeasure)
                : undefined,
              uomName: item?.unitOfMeasureName,
              unitPrice: item?.unitPrice,
            },
          }))
        );
        // UOM options derived from the material master itself — there is no
        // reliable org-level UOM list API in this app
        const uomMap = new Map<number, string>();
        activeMaterials?.forEach((item: any) => {
          const uomId = item?.unitOfMeasure ? Number(item?.unitOfMeasure) : undefined;
          if (uomId && item?.unitOfMeasureName && !uomMap.has(uomId)) {
            uomMap.set(uomId, item?.unitOfMeasureName);
          }
        });
        setUomOptions(
          Array.from(uomMap.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProjectOptions = async () => {
    try {
      const res = await projectsApis.getProjectList({
        page: 1,
        pageSize: 100,
        searchQuery: null,
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
        paginationId: null,
        projectStatus: "Active",
      });
      const list: any[] = res?.data?.list || [];
      setProjectOptions([
        { value: ORG_MASTER_PROJECT_ID, label: ORG_MASTER_LABEL },
        ...list.map((project: any) => ({
          value: Number(project?.projectId),
          label: project?.projectTitle || `Project ${project?.projectId}`,
        })),
      ]);
    } catch (error) {
      console.log("Error loading projects for norm scope:", error);
    }
  };

  const fetchDataById = async (id: any) => {
    try {
      resetWarningNorm();
      setMode("view");
      const apiResponse: ApiResponse = await normsApis.getNormById(id);
      const { data, status } = apiResponse;
      if (status) {
        setOriginalData(data);
        const { normCode, normTitle, remarks, normMaterials } = data;
        setValue("normCode", normCode);
        setValue("normTitle", normTitle);
        setValue("remarks", remarks);
        setValue("materials", mapNormMaterialsToForm(normMaterials));
      } else {
        console.log(apiResponse);
      }
    } catch (error) {
      console.log("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== "view") {
      fetchMaterialsList();
    }
  }, [mode]);

  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
  }, [id]);

  // CREATE mode only: load the project-scope options and prefill from the list's
  // selected scope (passed via extra.defaultValues). projectId is immutable once
  // created, so there is no scope field in edit/view.
  useEffect(() => {
    if (!id) {
      fetchProjectOptions();
      setValue(
        "projectId",
        extra?.defaultValues?.projectId ?? ORG_MASTER_PROJECT_ID
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildMaterialPayload = (material: NormMaterialType) => {
    const materialObject: any = {
      materialId: material.materialId,
      consumptionNorm: material.consumptionNorm,
    };
    if (material.uomId !== undefined && material.uomId !== null) {
      materialObject.uomId = material.uomId;
    }
    if (
      material.pucFactor !== undefined &&
      material.pucFactor !== null &&
      String(material.pucFactor) !== ""
    ) {
      materialObject.pucFactor = material.pucFactor;
    }
    const wastageFraction = wastagePercentToFraction(material.wastage);
    if (wastageFraction !== undefined) {
      materialObject.wastage = wastageFraction;
    }
    if (
      material.unitPrice !== undefined &&
      material.unitPrice !== null &&
      String(material.unitPrice) !== ""
    ) {
      materialObject.unitPrice = material.unitPrice;
    }
    return materialObject;
  };

  // Saved lines can only be toggled Active/Inactive — flip the field-array
  // row's status; updateForm diffs it against the originally loaded status.
  const toggleMaterialStatus = (index: number, record: NormMaterialType) => {
    const nextStatus = record.status === "Inactive" ? "Active" : "Inactive";
    update(index, { ...record, status: nextStatus });
  };

  const submitForm = (_formData: any) => {
    try {
      if (!_formData?.materials || _formData.materials.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "At least one material is required to create the norm.",
          confirmButtonText: "Ok",
        });
        return;
      }
      setLoading(true);
      resetWarningNorm();
      const finalPayload: any = {
        normCode: _formData.normCode,
        normTitle: _formData.normTitle,
        remarks: _formData.remarks,
        // Scope the new norm (0 = org master); immutable afterwards.
        projectId: _formData.projectId ?? ORG_MASTER_PROJECT_ID,
        materials: _formData.materials.map((material: NormMaterialType) =>
          buildMaterialPayload(material)
        ),
      };
      createNorm(finalPayload);
    } catch (error) {
      console.log("error", error);
    }
  };

  const updateForm = (_formData: any) => {
    try {
      resetWarningNorm();

      if (!_formData?.materials || _formData.materials.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "At least one material is required to update the norm.",
          confirmButtonText: "Ok",
        });
        return;
      }

      setLoading(true);
      // Saved lines are immutable: the only allowed change is a status toggle
      // (Activate/Deactivate). Value corrections = deactivate + add new line.
      const newMaterials: any = [];
      const updatedMaterials: any = [];
      _formData?.materials?.forEach((material: NormMaterialType) => {
        if (!material.normMaterialId) {
          newMaterials.push(buildMaterialPayload(material));
          return;
        }
        const original = originalData?.normMaterials?.find(
          (item) => item.normMaterialId === material.normMaterialId
        );
        const originalStatus = original?.status || "Active";
        const currentStatus = material.status || "Active";
        if (originalStatus !== currentStatus) {
          updatedMaterials.push({
            normMaterialId: material.normMaterialId,
            status: currentStatus,
          });
        }
      });
      const finalPayload: any = {
        normId: id,
        newMaterials,
        updatedMaterials,
        // Saved lines can never be deleted — deactivate instead. The key is
        // kept (empty) to match the API shape.
        deletedMaterials: [],
      };
      if (originalData?.normCode !== _formData.normCode) {
        finalPayload.normCode = _formData.normCode;
      }
      if ((originalData?.normTitle || "") !== (_formData.normTitle || "")) {
        finalPayload.normTitle = _formData.normTitle;
      }
      if ((originalData?.remarks || "") !== (_formData.remarks || "")) {
        finalPayload.remarks = _formData.remarks;
      }

      updateNorm(finalPayload);
    } catch (error) {}
  };

  const updateNorm = async (Payload: any) => {
    try {
      const response: ApiResponse = await normsApis.updateNorm(
        CustomJsonParse(Payload, 0, 5)
      );
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningNorm({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const createNorm = async (Payload: any) => {
    try {
      const response: ApiResponse = await normsApis.createNorm(
        CustomJsonParse(Payload, 0, 5)
      );
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        close();
      } else {
        setWarningNorm({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const activateNorm = async () => {
    try {
      const response: ApiResponse = await normsApis.activateNorm(Number(id));
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningNorm({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const deactivateNorm = async () => {
    try {
      const response: ApiResponse = await normsApis.deactivateNorm(Number(id));
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        fetchDataById(id);
      } else {
        setWarningNorm({
          message,
          error,
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  if (loading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }
  if (!loading && !originalData && id) {
    return (
      <Result
        status="500"
        title="500"
        subTitle="Sorry, something went wrong."
        extra={
          <Button
            className="btn btn-primary btn-sm"
            type="button"
            color="primary"
            onClick={() => {
              refresh();
              close();
            }}
          >
            <Icon name={"arrow-left"} />
            <span>Back</span>
          </Button>
        }
      />
    );
  }

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center mb-2 ">
        <h5 className="w-100 d-flex  align-items-center">
          {id
            ? `${originalData?.normCode}${
                originalData?.normTitle ? " - " + originalData?.normTitle : ""
              }`
            : "New Norm"}
          {id ? (
            <Popconfirm
              placement="right"
              title={<></>}
              icon={<></>}
              showCancel={false}
              trigger="hover"
              okButtonProps={{
                style: {
                  display: "none",
                },
              }}
              description={
                <div className="d-flex flex-column">
                  <Descriptions
                    title=""
                    bordered
                    column={1}
                    size="small"
                    className="custom-description-title"
                  >
                    <Descriptions.Item label="Status">
                      {originalData?.status}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">
                      {dayjs(originalData?.createdAt).format("DD-MMM-YYYY")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created By">
                      {originalData?.createdByUserName || "--"}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              }
            >
              <Icon
                name="info"
                className="ms-1"
                style={{
                  cursor: "pointer",
                }}
              />
            </Popconfirm>
          ) : (
            <></>
          )}
        </h5>
        {mode != "create" ? (
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2">
            {mode == "view" && (
              <>
                <li>
                  <ViewAccess moduleName="Norms">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="dropdown-toggle btn btn-icon btn-trigger"
                      >
                        <Icon name="more-v"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <ul className="link-list-opt">
                          {mode == "view" &&
                            originalData?.status == "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#tableView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setMode("edit");
                                  }}
                                >
                                  <span>Edit </span>
                                </DropdownItem>
                              </li>
                            )}
                          {mode == "view" &&
                            originalData?.status == "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#cardView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    deactivateNorm();
                                  }}
                                >
                                  <span>Deactivate</span>
                                </DropdownItem>
                              </li>
                            )}
                          {mode == "view" &&
                            originalData?.status !== "Active" && (
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#detailView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    activateNorm();
                                  }}
                                >
                                  <span>Activate</span>
                                </DropdownItem>
                              </li>
                            )}
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </ViewAccess>
                </li>
                <li>
                  <Button
                    className="btn btn-primary btn-sm"
                    type="button"
                    color="primary"
                    onClick={() => {
                      refresh();
                      close();
                    }}
                  >
                    <Icon name={"arrow-left"} />
                    <span>Back</span>
                  </Button>
                </li>
              </>
            )}
            {mode == "edit" && (
              <>
                <li>
                  <Button
                    className="btn btn-primary btn-sm"
                    type="button"
                    color="primary"
                    onClick={handleSubmit((data) => {
                      updateForm(data);
                    })}
                  >
                    <Icon name={"save"} />
                    <span>Save</span>
                  </Button>
                </li>
                <li>
                  <Button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    color="secondary"
                    onClick={() => {
                      setLoading(true);
                      setMode("view");
                      resettingFields(originalData);
                    }}
                  >
                    <span>Cancel</span>
                  </Button>
                </li>
              </>
            )}
          </ul>
        ) : (
          <></>
        )}
      </div>
      <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
        <Alert
          isOpen={originalData?.status == "Inactive"}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">Inactive:</span>{" "}
          <ReactTyped
            strings={[
              `This norm is currently inactive. Last updated on ${dayjs(
                originalData?.updatedAt
              ).format("DD-MMM-YYYY")} by "${
                originalData?.updatedByUserName || "N/A"
              }".`,
            ]}
            typeSpeed={5}
            showCursor={false}
          />
        </Alert>

        <Alert
          isOpen={warningNorm?.message != undefined}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">{warningNorm?.error}: </span>
          <ReactTyped
            strings={[warningNorm?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
        <FormProvider {...methods}>
          <form style={{ minHeight: "480px" }}>
            {mode === "create" && (
              <div className="row g-3 mb-1">
                <div className="col-4">
                  <FormSelect
                    name="projectId"
                    label="Scope"
                    placeholder="Select scope"
                    options={projectOptions}
                    showSearch
                  />
                </div>
              </div>
            )}
            <div className="row g-3">
              <div className="col-4">
                <FormInput
                  name="normCode"
                  label="Norm Code"
                  placeholder="Norm Code"
                  required
                  type="text"
                  disabled={mode === "view"}
                />
              </div>
              <div className="col-4">
                <FormInput
                  name="normTitle"
                  label="Norm Title"
                  placeholder="Norm Title"
                  type="text"
                  disabled={mode === "view"}
                />
              </div>
              <div className="col-4">
                <FormInput
                  name="remarks"
                  label="Remarks"
                  placeholder="Remarks"
                  type="text"
                  disabled={mode === "view"}
                />
              </div>
            </div>
            <div className="d-flex align-items-end justify-content-between mt-4 mb-3">
              <h6 className="mb-0">Materials</h6>
              <a
                onClick={() => {
                  setMaterialModalStatus((pre) => ({
                    ...pre,
                    open: true,
                  }));
                }}
                className="primary"
                style={{
                  fontSize: 16,
                  cursor: "pointer",
                  display: mode === "view" ? "none" : "",
                }}
              >
                <PlusSquareOutlined /> Add Material
              </a>
            </div>
            {mode === "edit" && (
              <div
                className="text-soft"
                style={{ fontSize: 12, marginTop: -8 }}
              >
                Saved material lines cannot be edited or deleted. To correct
                one, deactivate it and add a new line.
              </div>
            )}
            <div
              style={{
                width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                margin: "10px auto",
              }}
            >
              <Table
                rowKey={nanoid()}
                columns={[
                  {
                    title: "Material",
                    dataIndex: "materialName",
                    key: "materialName",
                    fixed: "left",
                    width: 240,
                    render: (text: any, record: NormMaterialType) => {
                      return <span>{text || record?.materialId}</span>;
                    },
                  },
                  {
                    title: "Material Code",
                    dataIndex: "materialCode",
                    key: "materialCode",
                    width: 140,
                    render: (text: any) => <span>{text || "-"}</span>,
                  },
                  {
                    title: "UOM",
                    dataIndex: "uomName",
                    key: "uomName",
                    width: 120,
                    render: (text: any, record: NormMaterialType) => (
                      <span>
                        {text ||
                          uomOptions?.find(
                            (option) => option.value == record?.uomId
                          )?.label ||
                          "-"}
                      </span>
                    ),
                  },
                  {
                    title: "Consumption Norm",
                    dataIndex: "consumptionNorm",
                    key: "consumptionNorm",
                    width: 150,
                    render: (value: any) =>
                      value !== undefined && value !== null && value !== ""
                        ? Number(value)
                        : "-",
                  },
                  {
                    title: "Wastage (%)",
                    dataIndex: "wastage",
                    key: "wastage",
                    width: 120,
                    render: (value: any) =>
                      value !== undefined && value !== null && value !== ""
                        ? `${Number(value)}%`
                        : "-",
                  },
                  {
                    title: "Unit Price (₹)",
                    dataIndex: "unitPrice",
                    key: "unitPrice",
                    width: 120,
                    render: (value: any) =>
                      value !== undefined && value !== null && value !== ""
                        ? Number(value)
                        : "-",
                  },
                  {
                    title: "PUC Factor",
                    dataIndex: "pucFactor",
                    key: "pucFactor",
                    width: 110,
                    render: (value: any) =>
                      value !== undefined && value !== null && value !== ""
                        ? Number(value)
                        : "-",
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 110,
                    render: (value: any, record: NormMaterialType) =>
                      record?.normMaterialId ? (
                        <Tag color={value === "Inactive" ? "red" : "green"}>
                          {value || "Active"}
                        </Tag>
                      ) : (
                        <Tag color="blue">New</Tag>
                      ),
                  },
                  {
                    title: "Action",
                    key: "action",
                    fixed: "right",
                    width: 110,
                    hidden: mode === "view",
                    render: (text, record: NormMaterialType, index) =>
                      record?.normMaterialId ? (
                        // Saved line: immutable — only Activate/Deactivate.
                        <Popconfirm
                          title={
                            record?.status === "Inactive"
                              ? "Activate this material?"
                              : "Deactivate this material?"
                          }
                          okText="Yes"
                          cancelText="No"
                          onConfirm={() => toggleMaterialStatus(index, record)}
                        >
                          <Button
                            size="sm"
                            color=""
                            className={
                              record?.status === "Inactive"
                                ? "text-success p-0"
                                : "text-danger p-0"
                            }
                          >
                            {record?.status === "Inactive"
                              ? "Activate"
                              : "Deactivate"}
                          </Button>
                        </Popconfirm>
                      ) : (
                        // Unsaved (new this session): full edit + remove.
                        <>
                          <Button
                            size="sm"
                            color=""
                            onClick={() => {
                              setMaterialModalStatus((pre) => ({
                                ...pre,
                                open: true,
                                title: "Edit Material",
                                data: {
                                  ...record,
                                },
                                index: index,
                              }));
                            }}
                          >
                            <Icon name="edit" />
                          </Button>
                          <Button
                            size="sm"
                            color=""
                            onClick={() => {
                              remove(index);
                            }}
                          >
                            <Icon name="trash" />
                          </Button>
                        </>
                      ),
                  },
                ]}
                onRow={(record: NormMaterialType) => ({
                  style:
                    record?.status === "Inactive" ? { opacity: 0.55 } : {},
                })}
                dataSource={fields}
                pagination={false}
                bordered
                size="middle"
                scroll={{
                  x: 1080,
                }}
              />
            </div>

            {mode == "create" && (
              <Row className="mt-4">
                <Col lg="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="button"
                        color="primary"
                        onClick={handleSubmit(submitForm)}
                        disabled={fields?.length == 0}
                      >
                        Submit
                      </Button>
                    </li>
                    <li>
                      <a
                        href="#cancel"
                        onClick={(ev) => {
                          ev.preventDefault();
                          close();
                        }}
                        className="link link-light"
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            )}
          </form>
        </FormProvider>
      </div>
      {materialModalStatus?.open ? (
        <NormMaterialModal
          open={materialModalStatus.open}
          title={materialModalStatus.title}
          mode={mode}
          append={(data: NormMaterialType) => {
            append(data);
          }}
          update={(index: any, data: NormMaterialType) => {
            // Only unsaved lines are ever edited via the modal — saved lines
            // are immutable, so no dirty-field diff is needed anymore.
            update(index, data);
          }}
          extra={{
            index: materialModalStatus.index,
            defaultData: materialModalStatus.data,
            sourceData: fields,
            materialOptions: materialOptions,
            uomOptions: uomOptions,
          }}
          onCancel={() =>
            setMaterialModalStatus({
              open: false,
              title: "Add New Material",
              data: { ...defaultMaterialData },
              index: undefined,
            })
          }
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default NormCreation;
